import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { StickyNote, Save } from "lucide-react";
import { eur, daysUntil, isoWeekRange, KANS_STATUS } from "./salesShared";

function KpiCard({ label, value, sub, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="kpi-card">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-800"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function NotesBoard() {
  const [text, setText] = useState("");
  const [meta, setMeta] = useState({ updated_by: null, updated_at: null });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sales/notes").then((r) => { setText(r.data.text || ""); setMeta({ updated_by: r.data.updated_by, updated_at: r.data.updated_at }); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/sales/notes", { text });
      setMeta({ updated_by: r.data.updated_by, updated_at: r.data.updated_at });
      setDirty(false);
      toast.success("Notitiebord opgeslagen");
    } catch (e) { toast.error("Kon notities niet opslaan"); setDirty(true); }
    finally { setSaving(false); }
  };

  const stamp = meta.updated_at
    ? `Laatst bijgewerkt door ${meta.updated_by || "-"} op ${new Date(meta.updated_at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
    : "Nog geen notities";

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="notes-board">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><StickyNote className="h-4 w-4 text-amber-600" /><span className="text-sm font-bold text-slate-800">Notitiebord — aandachtspunten</span></div>
        <button data-testid="notes-save" onClick={save} disabled={!dirty || saving}
          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-40">
          <Save className="h-3.5 w-3.5" /> {saving ? "Opslaan..." : "Opslaan"}
        </button>
      </div>
      <textarea
        data-testid="notes-textarea"
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        onBlur={() => { if (dirty) save(); }}
        rows={4}
        placeholder="Schrijf hier aandachtspunten voor het hele salesteam (zichtbaar voor iedereen met CRM-toegang)..."
        className="w-full resize-y rounded-md border border-amber-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-amber-400"
      />
      <div className="mt-1 text-[11px] text-slate-400">{stamp}</div>
    </div>
  );
}

export default function DashboardTab({ klanten, afspraken, acties, offertes, kansen, medewerkers, orders = [] }) {
  const openActies = acties.filter((a) => a.status !== "Gereed");
  const [wkStart, wkEnd] = isoWeekRange();
  const kpi = useMemo(() => {
    const teLaat = openActies.filter((a) => (daysUntil(a.vervaldatum) ?? 99) < 0).length;
    const bezoekenWeek = afspraken.filter((a) => a.datum >= wkStart && a.datum <= wkEnd).length;
    const kansOpen = kansen.filter((k) => k.status === "Lead" || k.status === "Offerte");
    const kansWaarde = kansOpen.reduce((s, k) => s + (Number(k.waarde) || 0), 0);
    const gewonnenWaarde = kansen.filter((k) => k.status === "Gewonnen").reduce((s, k) => s + (Number(k.waarde) || 0), 0);
    const perMed = medewerkers.map((m) => ({ naam: m.name, n: openActies.filter((a) => a.toegewezenId === m.id).length }));
    const perStatus = KANS_STATUS.map((s) => ({ s, n: kansen.filter((k) => k.status === s).length, w: kansen.filter((k) => k.status === s).reduce((x, k) => x + (Number(k.waarde) || 0), 0) }));
    return { teLaat, bezoekenWeek, kansOpenN: kansOpen.length, kansWaarde, gewonnenWaarde, perMed, perStatus };
  }, [acties, afspraken, kansen, medewerkers]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Sales-dashboard</h2>
      <NotesBoard />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Open acties" value={openActies.length} sub={`${kpi.teLaat} te laat`} tone={kpi.teLaat ? "text-rose-600" : "text-slate-800"} />
        <KpiCard label="Bezoeken deze week" value={kpi.bezoekenWeek} />
        <KpiCard label="Openstaande kansen" value={kpi.kansOpenN} sub={eur(kpi.kansWaarde)} />
        <KpiCard label="Gewonnen waarde" value={eur(kpi.gewonnenWaarde)} tone="text-emerald-600" />
        <KpiCard label="Offertes" value={offertes.length} sub={`${offertes.filter((o) => o.status === "Geaccepteerd").length} geaccepteerd`} />
        <KpiCard label="Orders" value={orders.length} sub={`${orders.filter((o) => o.status !== "Gefactureerd" && o.status !== "Geannuleerd").length} lopend`} tone="text-blue-700" />
        <KpiCard label="Klanten" value={klanten.length} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-700 mb-2">Open acties per medewerker</div>
          {kpi.perMed.length === 0 && <p className="text-sm text-slate-400">Geen medewerkers.</p>}
          {kpi.perMed.map((m) => (
            <div key={m.naam} data-testid="dash-per-med" className="flex items-center justify-between py-1 border-b border-slate-50 text-sm">
              <span className="text-slate-600">{m.naam}</span><Badge className="bg-blue-100 text-blue-700">{m.n}</Badge>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-700 mb-2">Pijplijn per status</div>
          {kpi.perStatus.map((s) => (
            <div key={s.s} data-testid="dash-per-status" className="flex items-center justify-between py-1 border-b border-slate-50 text-sm">
              <span className="text-slate-600">{s.s}</span><span className="text-slate-500">{s.n} · {eur(s.w)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
